import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle2, Loader2, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface CampoObrigatorioInlineProps {
  label: string;
  value: any;
  onChange: (value: any) => void;
  onBlur?: (value: any) => void;
  type?: 'text' | 'number' | 'date' | 'datetime-local' | 'textarea' | 'select' | 'month';
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  obrigatorio?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  erro?: string;
  className?: string;
  minLength?: number;
}

/**
 * Formata uma string de data ISO/Supabase para exibição em pt-BR
 * Ex: "2026-02-17T21:00" → "17/02/2026 21:00"
 */
function formatDateTimeBR(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const normalized = dateStr.replace(' ', 'T').substring(0, 16);
    const [datePart, timePart] = normalized.split('T');
    if (!datePart || !timePart) return dateStr;
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year} ${timePart}`;
  } catch {
    return dateStr;
  }
}

/**
 * Normaliza valor de data vindo do Supabase (espaço → T) para uso em input datetime-local
 */
function normalizeDatetimeLocal(val: string): string {
  if (!val) return '';
  return val.substring(0, 16).replace(' ', 'T');
}

export function CampoObrigatorioInline({
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  options = [],
  placeholder,
  obrigatorio = false,
  disabled = false,
  isLoading = false,
  erro,
  className,
  minLength
}: CampoObrigatorioInlineProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingDatetime, setIsEditingDatetime] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Calcular tamanho atual e validação de tamanho mínimo
  const tamanhoAtual = (type === 'textarea' || type === 'text') && typeof localValue === 'string' 
    ? localValue.length 
    : 0;
  const tamanhoMinimo = minLength || 0;
  const faltamCaracteres = tamanhoMinimo > 0 && tamanhoAtual > 0 && tamanhoAtual < tamanhoMinimo;
  
  const isPreenchido = localValue !== null && localValue !== undefined && localValue !== '' 
    && (!minLength || tamanhoAtual >= tamanhoMinimo);
  const temErro = obrigatorio && !isPreenchido;

  const handleChange = async (newValue: any) => {
    setLocalValue(newValue);
    
    if (type === 'text') {
      return; // text usa apenas onBlur para salvar
    }
    
    setIsSaving(true);
    try {
      await onChange(newValue);
    } finally {
      setTimeout(() => setIsSaving(false), 300);
    }
  };

  const handleDatetimeSave = async (newValue: string) => {
    setLocalValue(newValue);
    setIsEditingDatetime(false);
    setIsSaving(true);
    try {
      await onChange(newValue);
    } finally {
      setTimeout(() => setIsSaving(false), 300);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      if (type === 'text' || type === 'textarea') {
        setIsSaving(true);
        onBlur(localValue);
        setTimeout(() => setIsSaving(false), 500);
      } else if (localValue !== value) {
        onBlur(localValue);
      }
    }
  };

  const iconStatus = isSaving || isLoading ? (
    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
  ) : isPreenchido ? (
    <CheckCircle2 className="h-4 w-4 text-success" />
  ) : faltamCaracteres ? (
    <AlertCircle className="h-4 w-4 text-primary" />
  ) : obrigatorio ? (
    <AlertCircle className="h-4 w-4 text-destructive" />
  ) : null;

  const borderColor = temErro 
    ? 'border-destructive ring-2 ring-destructive/20' 
    : faltamCaracteres
    ? 'border-primary ring-1 ring-primary/20'
    : isPreenchido 
    ? 'border-success' 
    : '';

  // Datetime-local com modo fixo: se já tem valor salvo, mostra texto + lápis
  const isDatetimeWithValue = type === 'datetime-local' && localValue && !isEditingDatetime;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-1.5">
          {label}
          {obrigatorio && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              Obrigatório
            </Badge>
          )}
        </Label>
        {iconStatus}
      </div>

      {type === 'select' ? (
        <Select
          value={localValue || ''}
          onValueChange={handleChange}
          disabled={disabled || isLoading}
        >
          <SelectTrigger className={cn(borderColor)}>
            <SelectValue placeholder={placeholder || `Selecione ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : type === 'textarea' ? (
        <Textarea
          value={localValue || ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(borderColor, "min-h-[60px]")}
          rows={2}
        />
      ) : isDatetimeWithValue ? (
        /* Modo FIXO: data salva aparece como texto + botão de lápis */
        <div className={cn(
          "flex items-center gap-2 h-10 w-full rounded-md border-2 bg-background px-3 py-2",
          borderColor || "border-success"
        )}>
          <span className="text-sm font-medium flex-1">
            {formatDateTimeBR(localValue)}
          </span>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => setIsEditingDatetime(true)}
              title="Editar data"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ) : type === 'datetime-local' ? (
        /* Modo EDIÇÃO: input datetime-local */
        <Input
          type="datetime-local"
          value={normalizeDatetimeLocal(localValue || '')}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={(e) => {
            const val = e.target.value;
            if (val) {
              handleDatetimeSave(val);
            } else if (!val && localValue) {
              // Se limpou, volta ao valor anterior
              setIsEditingDatetime(false);
            }
          }}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(borderColor)}
          autoFocus={isEditingDatetime}
        />
      ) : (
        <Input
          type={type}
          value={localValue || ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(borderColor)}
        />
      )}

      {/* Contador de caracteres */}
      {minLength && (type === 'textarea' || type === 'text') && (
        <div className="flex items-center justify-between text-xs">
          <span className={cn(
            "font-medium",
            faltamCaracteres ? "text-primary" : isPreenchido ? "text-success" : "text-muted-foreground"
          )}>
            {tamanhoAtual}/{tamanhoMinimo} caracteres
          </span>
          {faltamCaracteres && (
            <span className="text-primary flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Faltam {tamanhoMinimo - tamanhoAtual} para fechamento
            </span>
          )}
        </div>
      )}

      {erro && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {erro}
        </p>
      )}
    </div>
  );
}
